// Tiny axis-less area sparkline for KPI tiles. No grid/axes/tooltip so it stays
// cheap to render even when many are on screen.

import { memo, useId } from "react";
import { Area, AreaChart, ResponsiveContainer, YAxis } from "recharts";

import { ANIMATION_ACTIVE } from "@/components/charts/theme";

export interface SparklineProps {
  data: readonly unknown[];
  dataKey: string;
  color?: string;
  height?: number;
}

function SparklineBase({ data, dataKey, color = "#E4E4E7", height = 40 }: SparklineProps) {
  // Unique gradient id per instance — keying by dataKey collided whenever two
  // sparklines on a page shared a dataKey (the second rendered with no fill).
  const gradId = `spark-${useId().replace(/:/g, "")}`;
  return (
    <ResponsiveContainer width="100%" height={height} debounce={60}>
      <AreaChart data={data as Record<string, unknown>[]} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.4} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        {/* Hidden axis just to give the area a stable domain. */}
        <YAxis hide domain={["dataMin", "dataMax"]} />
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={1.5}
          fill={`url(#${gradId})`}
          dot={false}
          isAnimationActive={ANIMATION_ACTIVE}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export const Sparkline = memo(SparklineBase);
